import { useAuthRestApi } from '../common';
import {
    ISignedDocument,
    IUserBulkUpdateSpecifier,
    IUserDetails,
    IUserEmailParam,
    IUsersApi
} from '@vivli/features/users/infrastructure/interface';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataTransformationService } from '@vivli/shared/infrastructure/service';
import { IApiRequestOptions } from '@vivli/core/infrastructure/interface';
import { IUser } from '@vivli/shared/infrastructure/interface';
import { IEnquiry } from '@vivli/features/enquiry/infastructure/interface';

export const useUsersApi = (): IUsersApi => {
    const { handleGet, handlePost, handlePut } = useAuthRestApi();
    const getUserDetails = (userId: string): Observable<IUserDetails> => {
        return handleGet<IUserDetails>(`/users/${userId}/detail/`).pipe(map(DataTransformationService.formatUserDetails));
    };

    const getUser = (userId: string, options?: IApiRequestOptions): Observable<IUser> => {
        return handleGet<IUser>(`/users/${userId}`, options);
    };

    const getUserOnLogin = (userId: string, options?: IApiRequestOptions): Observable<IUser> => {
        return handleGet<IUser>(`/users/setup/${userId}`, options);
    };

    const getUserByEmail = (emailParam: IUserEmailParam): Observable<IUser> => {
        return handlePost<IUser>(`/users/email`, emailParam);
    };

    const getUsers = (populateDataRequests: boolean): Observable<IUserDetails[]> => {
        return handleGet<IUserDetails[]>(`/users/list/${populateDataRequests}`).pipe(map(DataTransformationService.formatUsersDetails));
    };

    const setUserStatus = (userId: string, disable: boolean): Observable<void> => {
        return handleGet<void>(`/users/${userId}/${disable ? 'disable' : 'enable'}/`);
    };

    const setUserDaTraining = (userId: string, complete: boolean): Observable<string> => {
        return handlePut<string>(`/users/${userId}/completed?complete=${complete}`);
    };

    const getUserDataRequests = (userId: string): Observable<IDataRequest[]> => {
        return handleGet<IDataRequest[]>(`/datarequests/${userId}/user`);
    };

    const getUserEnquiries = (userId: string): Observable<IEnquiry[]> => {
        return handleGet<IEnquiry[]>(`/Enquiry/${userId}/user`);
    };

    const resetUserPassword = (userId: string): Observable<string> => {
        return handleGet<string>(`/users/${userId}/resetpassword/`);
    };

    const trackUserActivity = (token?: string): Observable<void> => {
        return handleGet<void>(`/users/trackactivity`, { token });
    };

    const setVivliAdminPermission = (userId: string, isVivliAdmin: boolean): Observable<IUserDetails> => {
        return handleGet<IUserDetails>(`/users/${userId}/admin?isVivliAdmin=${isVivliAdmin}`).pipe(
            map(DataTransformationService.formatUserDetails)
        );
    };

    const setInternalUserStatus = (userId: string, isInternalUser: boolean): Observable<IUserDetails> => {
        return handlePut<IUserDetails>(`/users/${userId}/internal?isInternalUser=${isInternalUser}`).pipe(
            map(DataTransformationService.formatUserDetails)
        );
    };

    const getVivliAdmins = (): Observable<IUserDetails[]> => {
        return handleGet<IUserDetails[]>('/users/listAdmins').pipe(map(DataTransformationService.formatUsersDetails));
    };

    const updateSignedDocuments = (
        userId: string,
        documentName: string,
        documentValue: string,
        documentType: string,
        userIntent: string
    ): Observable<ISignedDocument> => {
        return handlePost(`/users/${userId}/signedDocuments`, {
            documentName,
            documentValue,
            userIntent,
            documentType
        });
    };

    const updateUserNote = (userId: string, note: string): Observable<IUser> => {
        return handlePut(`/users/${userId}/note/${note}`);
    };

    const updateDaTrainingCompletedDate = (userId: string, completedDate: Date): Observable<IUser> => {
        return handlePut(`/users/${userId}/completedDate/${completedDate}`);
    };

    const getUpdatePackage = (): Observable<string> => {
        return handlePost<string>('/users/getupdatepackage');
    };

    const processBulkUpdate = (updateSpec: IUserBulkUpdateSpecifier): Observable<void> => {
        return handlePost<void>('/users/bulkupdateforusers', updateSpec);
    };

    return {
        getUserDetails,
        getUser,
        getUserOnLogin,
        getUserByEmail,
        getUsers,
        setUserStatus,
        setUserDaTraining,
        getUserDataRequests,
        getUserEnquiries,
        resetUserPassword,
        trackUserActivity,
        setVivliAdminPermission,
        setInternalUserStatus,
        getVivliAdmins,
        updateSignedDocuments,
        updateUserNote,
        updateDaTrainingCompletedDate,
        getUpdatePackage,
        processBulkUpdate
    };
};
